import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useStudentProfileStore } from '../../stateManagers/stores/studentProfileStore';
import AnnouncementDetailsModal from './AnnouncementDetailsModal';

/* ─── Design Tokens ────────────────────────────────────────────────── */
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyHover:   'rgba(14,34,93,0.06)',
  gold:        '#fbb21f',
  goldDim:     'rgba(251,178,31,0.14)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  bg:          '#f7f8fa',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
};

/* ─── Types ─────────────────────────────────────────────────────────── */
interface Batch {
  batch_id:    string;
  batch_name:  string;
  course_name?: string | null;
}

interface AnnouncementFile {
  file_name: string;
  key:       string;
  url:       string;
  size:      number;
  mime_type: string;
  uploaded_at: string;
}

interface Announcement {
  announcement_id:    string;
  institute_id:       string;
  institute_name:     string;
  branch_id:          string;
  branch_name:        string;
  batch_id:           string | null;
  batch_name:         string | null;
  announcement_title: string;
  announcement_body:  string;
  files:              AnnouncementFile[];
  created_at:         string;
  created_by:         string;
  created_by_name:    string;
}

interface ApiResponse {
  success:    boolean;
  data:       Announcement[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

const formatDate = (dateString: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/* ─── Component ─────────────────────────────────────────────────────── */
const AnnouncementsList: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState<string | null>(null);
  const [page,           setPage]           = useState(1);
  const [limit,          setLimit]          = useState(10);
  const [pagination,     setPagination]     = useState({ total: 0, page: 1, limit: 10, pages: 0 });

  // Batch chips state — multi-select, mirrors mobile's batch_ids[] filtering
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);

  // Modal state
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<string | null>(null);
  const [modalVisible,           setModalVisible]           = useState(false);

  const { profile } = useStudentProfileStore();
  const branchId = profile?.branch?.branch_id ?? null;
  const isEnrolled = (profile as any)?.is_enrolled ?? false;

  const batches: Batch[] = (profile?.enrollments ?? [])
    .filter((e: any) => e.batch_id !== null && e.batch_id !== undefined && e.batch_id.trim() !== '')
    .map((e: any) => ({
      batch_id:    e.batch_id as string,
      batch_name:  e.batch_name ?? 'Batch',
      course_name: e.course_name ?? null,
    }));

  // Initialize with all batch IDs selected once profile/batches are available
  useEffect(() => {
    if (branchId && batches.length > 0 && selectedBatchIds.length === 0) {
      setSelectedBatchIds(batches.map(b => b.batch_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, batches.length]);

  useEffect(() => {
    if (branchId) fetchAnnouncements();
  }, [page, limit, selectedBatchIds, branchId]);

  const fetchAnnouncements = async () => {
    if (!branchId) {
      setError('Branch information not available');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (isEnrolled && selectedBatchIds.length > 0) {
        selectedBatchIds.forEach(id => params.append('batch_ids[]', id));
      }

      const res = await apiClient.get<ApiResponse>(`/v1/announcements/by-branch/${branchId}?${params}`);
      if (res.data.success) {
        setAnnouncements(res.data.data);
        setPagination(res.data.pagination);
      } else {
        setError('Failed to load announcements');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const allBatchesSelected = batches.length > 0 && batches.every(b => selectedBatchIds.includes(b.batch_id));

  const handleSelectAllBatches = () => {
    if (allBatchesSelected) return;
    setSelectedBatchIds(batches.map(b => b.batch_id));
    setPage(1);
  };

  const handleToggleBatch = (batchId: string) => {
    setSelectedBatchIds(prev => {
      if (prev.includes(batchId)) {
        if (prev.length === 1) return prev; // keep at least one selected
        return prev.filter(id => id !== batchId);
      }
      return [...prev, batchId];
    });
    setPage(1);
  };

  const handleAnnouncementClick = (announcement: Announcement) => {
    setSelectedAnnouncementId(announcement.announcement_id);
    setModalVisible(true);
  };

  const handleModalHide = () => {
    setModalVisible(false);
    setSelectedAnnouncementId(null);
  };

  /* ── Cell renderers ─────────────────────────────────────────────── */
  const titleTemplate = (row: Announcement) => {
    const displayBatchName = row.batch_name
      ? row.batch_name.length > 15
        ? row.batch_name.substring(0, 15) + '...'
        : row.batch_name
      : null;

    return (
      <div>
        <div style={{ fontWeight: '700', color: C.navy, fontSize: '0.84rem', marginBottom: '2px' }}>{row.announcement_title}</div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem',
          color: displayBatchName ? C.sub : C.green,
          background: displayBatchName ? C.navyHover : C.greenDim,
          border: `1px solid ${displayBatchName ? 'rgba(14,34,93,0.12)' : C.greenBorder}`,
          borderRadius: '12px', padding: '1px 8px',
        }}>
          <i className={`pi ${displayBatchName ? 'pi-users' : 'pi-globe'}`} style={{ fontSize: '0.65rem' }} />
          {displayBatchName ?? 'All Batches'}
        </span>
      </div>
    );
  };

  const bodyTemplate = (row: Announcement) => (
    <div style={{
      fontSize: '0.79rem', color: C.sub, maxWidth: '340px',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
      {row.announcement_body || <span style={{ color: C.muted }}>—</span>}
    </div>
  );

  const filesTemplate = (row: Announcement) => {
    const count = row.files?.length ?? 0;
    if (count === 0) return <span style={{ color: C.muted, fontSize: '0.8rem' }}>—</span>;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: '#a06000', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '10px', padding: '2px 8px', fontWeight: '700' }}>
        <i className="pi pi-paperclip" style={{ fontSize: '0.65rem' }} />
        {count}
      </span>
    );
  };

  const dateTemplate = (row: Announcement) => (
    <div style={{ fontSize: '0.82rem', color: C.body, fontWeight: '600' }}>
      <i className="pi pi-calendar" style={{ fontSize: '0.7rem', marginRight: '4px', color: C.muted }} />
      {formatDate(row.created_at)}
    </div>
  );

  const actionTemplate = (row: Announcement) => (
    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
      <button
        onClick={() => handleAnnouncementClick(row)}
        title="View"
        style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, transition: 'all 0.15s ease' }}
        onMouseEnter={e => { e.currentTarget.style.background = C.navyHover; e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)'; e.currentTarget.style.color = C.navy; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
      >
        <i className="pi pi-eye" style={{ fontSize: '0.78rem' }} />
      </button>
    </div>
  );

  /* ── Pagination ─────────────────────────────────────────────────── */
  const renderPagination = () => {
    const total = pagination.pages;
    const pages: (number | string)[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', total);
    } else if (page >= total - 2) {
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      pages.push(1, '...', page, '...', total);
    }
    return pages.map((p, i) =>
      p === '...' ? (
        <span key={`e${i}`} style={{ padding: '0 4px', color: C.muted, fontSize: '0.82rem' }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{ minWidth: '32px', height: '32px', padding: '0 8px', borderRadius: '7px', fontSize: '0.82rem', fontWeight: page === p ? '700' : '500', background: page === p ? C.navy : 'transparent', color: page === p ? C.gold : C.navy, border: page === p ? 'none' : `1px solid ${C.border}`, boxShadow: page === p ? '0 2px 6px rgba(14,34,93,0.25)' : 'none', cursor: 'pointer', transition: 'all 0.15s ease' }}
          onMouseEnter={e => { if (page !== p) e.currentTarget.style.background = C.navyHover; }}
          onMouseLeave={e => { if (page !== p) e.currentTarget.style.background = 'transparent'; }}
        >
          {p}
        </button>
      )
    );
  };

  /* ── Loading skeleton ───────────────────────────────────────────── */
  if (loading && announcements.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading announcements…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .an-datatable .p-datatable-thead > tr > th {
          background: #fafafa !important;
          color: ${C.muted} !important;
          font-size: 0.7rem !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          border-bottom: 2px solid ${C.border} !important;
          padding: 12px 14px !important;
        }
        .an-datatable .p-datatable-tbody > tr { transition: background 0.12s; cursor: pointer; }
        .an-datatable .p-datatable-tbody > tr:hover > td { background: rgba(14,34,93,0.025) !important; }
        .an-datatable .p-datatable-tbody > tr > td {
          border-bottom: 1px solid #f3f4f6 !important;
          padding: 10px 12px !important;
          font-size: 0.83rem;
        }
        .an-batch-chips { display: flex; gap: 6px; flex-wrap: wrap; overflow-x: auto; padding-bottom: 2px; }
        .an-batch-chips::-webkit-scrollbar { height: 3px; }
        .an-batch-chips::-webkit-scrollbar-track { background: transparent; }
        .an-batch-chips::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
        .an-batch-chip { white-space: nowrap; transition: all 0.15s ease; }
        .an-batch-chip:hover { transform: translateY(-1px); }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.88rem' }} />}

        {/* ── Batch Chips ─────────────────────────────────────────────── */}
        {isEnrolled && batches.length > 0 && (
          <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '12px 16px', boxShadow: '0 1px 4px rgba(14,34,93,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                <i className="pi pi-users" style={{ fontSize: '0.72rem', color: C.muted }} />
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Batch
                </span>
              </div>
              <div style={{ width: '1px', height: '16px', background: C.border, flexShrink: 0 }} />

              <div className="an-batch-chips">
                <button
                  className="an-batch-chip"
                  onClick={handleSelectAllBatches}
                  style={{
                    padding: '4px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer',
                    border: allBatchesSelected ? 'none' : `1px solid ${C.border}`,
                    background: allBatchesSelected ? C.navy : 'transparent',
                    color: allBatchesSelected ? C.gold : C.sub,
                    boxShadow: allBatchesSelected ? '0 2px 8px rgba(14,34,93,0.2)' : 'none',
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                  }}
                >
                  {allBatchesSelected && <i className="pi pi-check" style={{ fontSize: '0.62rem' }} />}
                  All Batches
                </button>

                {batches.map(b => {
                  const isActive = selectedBatchIds.includes(b.batch_id);
                  return (
                    <button
                      key={b.batch_id}
                      className="an-batch-chip"
                      onClick={() => handleToggleBatch(b.batch_id)}
                      style={{
                        padding: '4px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer',
                        border: isActive ? 'none' : `1px solid ${C.border}`,
                        background: isActive ? C.navy : 'transparent',
                        color: isActive ? C.gold : C.sub,
                        boxShadow: isActive ? '0 2px 8px rgba(14,34,93,0.2)' : 'none',
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                      }}
                    >
                      {isActive && <i className="pi pi-check" style={{ fontSize: '0.62rem' }} />}
                      {b.batch_name}
                      {b.course_name && (
                        <span style={{ fontSize: '0.65rem', fontWeight: '500', opacity: 0.7, marginLeft: '1px' }}>
                          · {b.course_name}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Not-enrolled banner ─────────────────────────────────────── */}
        {!isEnrolled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: '12px', padding: '12px 16px' }}>
            <i className="pi pi-info-circle" style={{ color: '#0891b2', fontSize: '1rem' }} />
            <span style={{ fontSize: '0.82rem', color: '#0e7490', fontWeight: '600' }}>
              You're not enrolled in a batch — showing all general announcements.
            </span>
          </div>
        )}

        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>

          {/* ── Toolbar ─────────────────────────────────────────────── */}
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.8rem', color: C.sub }}>Showing</span>
              <select
                value={limit}
                onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                style={{ padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: '7px', fontSize: '0.8rem', color: C.navy, background: C.white, fontWeight: '600', cursor: 'pointer', outline: 'none' }}
              >
                {[5, 10, 20, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* ── DataTable ───────────────────────────────────────────── */}
          <DataTable
            value={announcements}
            dataKey="announcement_id"
            loading={loading}
            onRowClick={e => handleAnnouncementClick(e.data as Announcement)}
            emptyMessage={
              <div style={{ textAlign: 'center', padding: '40px 20px', color: C.muted }}>
                <i className="pi pi-megaphone" style={{ fontSize: '2rem', display: 'block', marginBottom: '10px', opacity: 0.4 }} />
                <span style={{ fontSize: '0.9rem' }}>No announcements found</span>
              </div>
            }
            className="an-datatable"
            style={{ fontSize: '0.83rem' }}
          >
            <Column header="Title / Batch" body={titleTemplate} headerStyle={{ minWidth: '220px' }} />
            <Column header="Message"       body={bodyTemplate}  headerStyle={{ minWidth: '220px' }} />
            <Column header="Files"         body={filesTemplate} headerStyle={{ minWidth: '90px' }} />
            <Column header="Posted"        body={dateTemplate}  headerStyle={{ minWidth: '130px' }} />
            <Column
              header="Created By"
              body={(row: Announcement) => (
                <span style={{ color: C.sub, fontSize: '0.82rem' }}>{row.created_by_name ?? '—'}</span>
              )}
              headerStyle={{ minWidth: '130px' }}
            />
            <Column header="Actions" body={actionTemplate} headerStyle={{ width: '80px', textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} />
          </DataTable>

          {/* ── Pagination footer ───────────────────────────────────── */}
          {pagination.pages > 0 && (
            <div style={{ padding: '14px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <span style={{ fontSize: '0.8rem', color: C.muted }}>
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} announcements
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <button onClick={() => setPage(1)} disabled={page === 1} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-double-left" style={{ fontSize: '0.7rem' }} />
                </button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-left" style={{ fontSize: '0.7rem' }} />
                </button>

                {renderPagination()}

                <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-right" style={{ fontSize: '0.7rem' }} />
                </button>
                <button onClick={() => setPage(pagination.pages)} disabled={page === pagination.pages} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                  <i className="pi pi-angle-double-right" style={{ fontSize: '0.7rem' }} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AnnouncementDetailsModal
        visible={modalVisible}
        announcementId={selectedAnnouncementId}
        onHide={handleModalHide}
      />
    </>
  );
};

export default AnnouncementsList;
